import type { CategoryModel, ProductGroupModel } from '@/shared/generated'
import type { Forest } from '@/shared/lib/forest'
import type { CategoryCatalog } from './catalog'

import { createSubforest } from '@/shared/lib/forest'

import { byName } from './catalog'
import { reportCatalogIssue } from './report'

export type ProductGroupTree = {
	id: string
	name: string
	forest: Forest<CategoryModel>
	unknownCategories: readonly string[]
}

export type ProductGroupCatalog = {
	catalog: CategoryCatalog
	groups: ReadonlyMap<string, ProductGroupTree>
}

/**
 * Projects every PG onto the shared catalog. Each PG's tree is `full_categories` plus
 * their catalog ancestors, so the full path renders even when the API omits them.
 */
export const createProductGroupCatalog = (
	catalog: CategoryCatalog,
	productGroups: ProductGroupModel[]
): ProductGroupCatalog => {
	const groups = new Map<string, ProductGroupTree>()
	for (const productGroup of [...productGroups].sort(byName)) {
		const fullCategories = productGroup.full_categories ?? []
		const unknownCategories = fullCategories.filter((id) => !catalog.has(id))
		if (unknownCategories.length) {
			reportCatalogIssue('unknown_pg_categories', {
				productGroupId: productGroup.id,
				ids: unknownCategories,
			})
		}
		groups.set(productGroup.id, {
			id: productGroup.id,
			name: productGroup.name,
			// full_categories may list only deep nodes (no ancestors), so add the
			// catalog ancestors of every known member to keep the full path.
			forest: createSubforest(
				catalog,
				fullCategories.flatMap((id) =>
					catalog.has(id) ? [id, ...catalog.ancestors(id)] : []
				)
			),
			unknownCategories,
		})
	}

	return { catalog, groups }
}
